/**
 * A page layout's FORM (KPR-1860, KPE-041), as pure rules: which sheets a body
 * has, which blocks are on which, and what a printer has to be told.
 *
 * `body.form` is the server's (keepr-api docs/SCHEMA.md § A form on a page
 * layout). A form is a set of page images, one sheet per page, and a block
 * belongs to the sheet its `formPage` names: absent is page 1, because a form
 * attached later needs no rewrite. On a layout with no form there is one sheet,
 * `body.page` itself, and every block is on it, so a plain layout runs through
 * here unchanged.
 */

/** The API's own limit, checked first so a file too large is not sent at all. */
export const MAX_FORM_BYTES = 25 * 1024 * 1024;
export const MAX_FORM_MEGABYTES = 25;

/** What the file picker offers. The bytes decide, on the server. */
export const FORM_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

export const hasForm = body => Array.isArray(body?.form?.pages) && body.form.pages.length > 0;

/** The page a block sits on, 1-based. */
export const blockPage = block => block?.formPage || 1;

/**
 * Every sheet this body draws: `{ number, width, height }`. An image that has
 * not said how big it is has null sizes until the person chooses, and until
 * then fills the sheet the layout already has.
 */
export function formSheets(body) {
	const page = body?.page;
	if (!hasForm(body)) return [{ number: 1, width: page?.width, height: page?.height }];
	return body.form.pages.map((sheet, index) => ({
		number: index + 1,
		width: sheet.width || page?.width,
		height: sheet.height || page?.height,
	}));
}

/**
 * `body.page` as one sheet of the form sees it: its own width and height, the
 * layout's unit and margins. Every box function asks this, so a block is kept
 * inside the sheet it is on. No form: `body.page` itself.
 */
export function sheetPage(body, formPage = 1) {
	if (!hasForm(body)) return body.page;
	const sheet = formSheets(body)[formPage - 1];
	return sheet ? { ...body.page, width: sheet.width, height: sheet.height } : body.page;
}

/** The blocks one sheet draws. No form: all of them, whatever they carry. */
export function blocksOnSheet(body, number) {
	const blocks = body?.blocks || [];
	return hasForm(body) ? blocks.filter(block => blockPage(block) === number) : blocks;
}

/**
 * The blocks a sheet cannot draw where they are: after a form with fewer pages
 * or smaller paper replaced the last, on a page the form lacks (`no_such_page`)
 * or off their page's sides (`off_sheet`). The API's own test (`blocksOffForm` in
 * keepr-api utils/layoutForms.js), run on the body in hand, because
 * `changes.offForm` knows only the stored blocks and only at that moment.
 *
 * A plain layout is asked too, of its one sheet: taking a form off keeps the paper
 * of page 1, so a block a larger page held can be off it, and a plain save refuses
 * that block until it moves. It may be clipped out of reach, so being told is the
 * person's only way to it. No other plain layout has one: the API refuses them.
 */
export function offFormBlocks(body) {
	const onForm = hasForm(body);
	const sheets = formSheets(body);
	const out = [];
	for (const block of body.blocks || []) {
		const formPage = onForm ? blockPage(block) : 1;
		const sheet = sheets[formPage - 1];
		if (!sheet) out.push({ id: block.id, formPage, why: 'no_such_page' });
		else if (block.x + block.w > sheet.width + 1e-9 || block.y >= sheet.height) out.push({ id: block.id, formPage, why: 'off_sheet' });
	}
	return out;
}

/**
 * The `@page` rules a body needs. `@page` cannot read a custom property, so
 * these reach the printer through a <style> the print route writes. Margin 0:
 * the layout's own margins are already inside the sheet's coordinates, and a
 * second set from the browser would shift every block.
 *
 * A PDF's pages can differ in size. Each page unlike the first gets a named
 * `@page` and a `page` rule, or it would print on the first page's paper and be
 * cut. A plain layout gets exactly its one rule.
 */
export function sheetPageRules(body) {
	const { unit } = body.page;
	const size = sheet => `size: ${sheet.width}${unit} ${sheet.height}${unit}; margin: 0;`;
	const [first, ...rest] = formSheets(body);
	const rules = [`@page { ${size(first)} }`];
	for (const sheet of rest) {
		if (sheet.width === first.width && sheet.height === first.height) continue;
		rules.push(`@page keepr-sheet-${sheet.number} { ${size(sheet)} }`);
		rules.push(`.page-sheet[data-form-page="${sheet.number}"] { page: keepr-sheet-${sheet.number}; }`);
	}
	return rules.join(' ');
}

/**
 * One step of the editor's working copy, with the server's form and paper. A
 * form changes on the server at once, so it is not an undo step: every step
 * in the history takes it, and no undo can bring back a paper the API refuses
 * (`form_sets_paper_size`). Taking a form off drops every `formPage`, as the
 * API does: a plain layout has one sheet.
 */
export function adoptForm(body, stored) {
	const { form: _form, ...rest } = body;
	if (stored?.form) return { ...rest, page: stored.page, form: stored.form };
	return {
		...rest,
		page: stored?.page || rest.page,
		blocks: (rest.blocks || []).map(({ formPage: _page, ...block }) => block),
	};
}

/**
 * Which way up a sheet is. A custom sheet's stored `orientation` says nothing: the
 * API stores `portrait` for every custom form sheet, with its numbers already
 * laid out, so its shape is the answer. A named size says what it was asked.
 */
export const sheetOrientation = page => (page.size === 'custom' ? (page.width > page.height ? 'landscape' : 'portrait') : page.orientation);

/**
 * The body of `PATCH …/form`: `{ size, orientation }` or a custom sheet. A
 * stored `width` and `height` are the laid-out ones, while the API reads a custom
 * size as an upright sheet it then turns, so a custom size is sent upright.
 */
export function paperPatch(page, { size = page.size, orientation = sheetOrientation(page), width, height } = {}) {
	if (size !== 'custom') return { size, orientation };
	const turned = orientation !== sheetOrientation(page);
	const across = width ?? (turned ? page.height : page.width);
	const down = height ?? (turned ? page.width : page.height);
	return orientation === 'landscape'
		? { size, width: down, height: across, unit: page.unit, orientation }
		: { size, width: across, height: down, unit: page.unit, orientation };
}

/** What a person is told about the form: its pages and its paper. */
export function formSummary(body) {
	if (!hasForm(body)) return '';
	const sheets = formSheets(body);
	const count = sheets.length === 1 ? '1 page' : `${sheets.length} pages`;
	if (body.form.paper === 'unset') return `${count}, no paper size yet`;
	const same = sheets.every(sheet => sheet.width === sheets[0].width && sheet.height === sheets[0].height);
	return same ? `${count}, ${sheets[0].width} x ${sheets[0].height} ${body.page.unit}` : `${count}, of more than one size`;
}

/** The sheet under a point, from the sheets' rectangles; null in the gap between two, or off them. */
export function sheetAtPoint(sheets, x, y) {
	return sheets.find(({ rect }) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) || null;
}
